package xyz.malefic.gallery.ui

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.css.Cursor.Companion.Pointer
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.ObjectFit.Companion.Cover
import com.varabyte.kobweb.compose.css.Transition.Companion.of
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment.CenterHorizontally
import com.varabyte.kobweb.compose.ui.Alignment.CenterVertically
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.boxShadow
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.flexWrap
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.objectFit
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.transform
import com.varabyte.kobweb.compose.ui.modifiers.transition
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.layout.Surface
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.selectors.hover
import com.varabyte.kobweb.silk.style.toModifier
import kotlinx.browser.window
import kotlinx.serialization.Serializable
import org.jetbrains.compose.web.css.CSSSizeValue
import org.jetbrains.compose.web.css.CSSUnit
import org.jetbrains.compose.web.css.FlexWrap
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.s
import xyz.malefic.gallery.ui.ThemeColors.border
import xyz.malefic.gallery.ui.ThemeColors.hover
import xyz.malefic.gallery.ui.ThemeColors.surface
import xyz.malefic.gallery.ui.ThemeColors.textSecondary
import xyz.malefic.gallery.ui.ThemeManager.isDarkMode
import xyz.malefic.gallery.ui.ThemeManager.toggleTheme
import com.varabyte.kobweb.compose.css.Transition.Companion.of as transitionOf

@Serializable
data class Folder(
    val name: String,
    val images: List<String>,
    val folders: List<Folder>,
)

@Serializable
data class GalleryIndex(
    val name: String,
    val images: List<String>,
    val folders: List<Folder>,
)

val ImageCardStyle =
    CssStyle {
        base {
            Modifier
                .padding(12.px)
                .border(1.px, LineStyle.Solid, border)
                .borderRadius(12.px)
                .backgroundColor(surface)
                .defaultBoxShadow()
                .transition(
                    transitionOf("transform", 0.3.s),
                    transitionOf("box-shadow", 0.3.s),
                ).cursor(Pointer)
        }
        hover {
            Modifier
                .transform { scale(1.03) }
                .boxShadow(offsetX = 0.px, offsetY = 8.px, blurRadius = 16.px, color = border)
        }
    }

@Composable
fun ImageCard(
    imagePath: String,
    onClick: () -> Unit,
) {
    Surface(
        ImageCardStyle
            .toModifier()
            .onClick { onClick() },
    ) {
        Column(
            modifier = Modifier.width(250.px),
            horizontalAlignment = CenterHorizontally,
        ) {
            Image(
                src = imagePath,
                modifier =
                    Modifier
                        .width(230.px)
                        .height(230.px)
                        .objectFit(Cover)
                        .borderRadius(8.px),
            )
            SpanText(
                imagePath.substringAfterLast("/"),
                modifier =
                    Modifier
                        .padding(12.px)
                        .fontSize(14.px)
                        .color(textSecondary),
            )
        }
    }
}

@Composable
fun FolderView(
    folder: Folder,
    basePath: String,
    expandedState: MutableMap<String, Boolean>,
) {
    val folderKey = "$basePath/${folder.name}"
    val isExpanded = expandedState[folderKey] == true

    // Check if this is the DM folder that needs password protection
    val isPasswordProtected = isFolderPasswordProtected(folder.name)

    // State for showing password dialog
    var showPasswordDialog by remember { mutableStateOf(false) }

    // Check if the password is already saved in localStorage
    val isPasswordSaved = isPasswordProtected && PasswordManager.isPasswordSaved() or !isPasswordProtected

    Surface(
        modifier =
            Modifier
                .fillMaxWidth()
                .padding(24.px)
                .margin(bottom = 16.px)
                .borderRadius(8.px)
                .backgroundColor(if (isExpanded) hover else surface)
                .transition(of("background-color", 0.2.s)),
    ) {
        Column(Modifier.fillMaxWidth()) {
            Row(
                modifier =
                    Modifier
                        .fillMaxWidth()
                        .cursor(Pointer)
                        .onClick {
                            if (isPasswordProtected && !isPasswordSaved) {
                                // Show password dialog if folder is protected and password not saved
                                showPasswordDialog = true
                            } else {
                                // Otherwise toggle expanded state as normal
                                expandedState[folderKey] = !isExpanded
                            }
                        },
                verticalAlignment = CenterVertically,
            ) {
                SpanText(
                    if (isPasswordProtected) "🔒" else "📁",
                    modifier =
                        Modifier
                            .fontSize(20.px)
                            .padding(right = 8.px),
                )
                SpanText(
                    folder.name,
                    modifier =
                        Modifier
                            .fontSize(18.px)
                            .fontWeight(FontWeight.Medium)
                            .color(ThemeColors.text),
                )
                Spacer()
                SpanText(
                    if (isExpanded) "▼" else "▶",
                    modifier =
                        Modifier
                            .fontSize(14.px)
                            .padding(left = 8.px)
                            .color(textSecondary),
                )
            }

            // Use window.prompt instead of PasswordDialog
            if (showPasswordDialog) {
                console.log("Showing password prompt")
                // Use window.prompt to get the password
                val promptResult = window.prompt("This folder requires a password to view its contents.", "")

                // Handle the result
                showPasswordDialog = false
                if (promptResult != null) {
                    val success = PasswordManager.checkPassword(promptResult)
                    if (success) {
                        // If password is correct, save it and expand the folder
                        PasswordManager.savePassword(promptResult)
                        expandedState[folderKey] = true
                    } else {
                        // If password is incorrect, clear it
                        PasswordManager.clearPassword()
                    }
                }
            }

            if (isExpanded) {
                folder.folders.forEach { subfolder ->
                    FolderView(subfolder, "$basePath/${folder.name}", expandedState)
                }

                Row(
                    modifier =
                        Modifier
                            .fillMaxWidth()
                            .padding(top = 16.px, left = 24.px)
                            .flexWrap(FlexWrap.Wrap),
                ) {
                    folder.images.forEach { image ->
                        val imagePath = "$basePath/${folder.name}/$image"
                        ImageCard(imagePath) {
                            window.open(imagePath, "_blank")
                        }
                    }
                }
            }
        }
    }
}

@Composable
fun ThemeToggleButton() {
    Surface(
        modifier =
            Modifier
                .padding(8.px)
                .borderRadius(20.px)
                .cursor(Pointer)
                .backgroundColor(surface)
                .defaultBoxShadow()
                .onClick { toggleTheme() },
    ) {
        SpanText(
            if (isDarkMode) "🌞" else "🌙",
            modifier = Modifier.fontSize(24.px).padding(8.px),
        )
    }
}

@Composable
fun Spacer(modifier: Modifier = Modifier) {
    Box(modifier = modifier.width(window.innerWidth.px * 0.75))
}

fun Modifier.defaultBoxShadow() = boxShadow(offsetX = 0.px, offsetY = 4.px, blurRadius = 12.px, color = border)

operator fun CSSSizeValue<CSSUnit.px>.times(number: Number) = (number.toDouble() * value).px

operator fun CSSSizeValue<CSSUnit.px>.times(px: CSSSizeValue<CSSUnit.px>) = (px.value * value).px
